import {
  Box,
  Chip,
  Button,
  Dialog,
  Tooltip,
  Backdrop,
  useTheme,
  TextField,
  FormLabel,
  Typography,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import { Project } from 'library/types';
import { useEffect, useState } from 'react';
import apiService from 'library/api-service';
import { CompanyToBeCreated } from 'pages/book';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';

type RelatedContact = {
  nameSurname: string;
  email: string;
  phoneNumber: string;
};

type CreateEffortRequestFormParams = {
  bookId: Book | null;
  companyVkn: CompanyToBeCreated | null;
  projectId: Project | null;
  explanation: string;
  effortDay: string;
  estimatedStartDate: Dayjs | null;
  estimatedEndDate: Dayjs | null;
  customerFullName: string;
  customerEmail: string;
  relatedContactEmails: RelatedContact[];
};

export type CreateEffortRequestPostFormParams = Omit<
  CreateEffortRequestFormParams,
  | 'bookId'
  | 'companyVkn'
  | 'projectId'
  | 'effortDay'
  | 'estimatedStartDate'
  | 'estimatedEndDate'
  | 'relatedContactEmails'
> & {
  bookId: number;
  companyVkn: string;
  projectId: number;
  effortDay: number;
  estimatedStartDate: string;
  estimatedEndDate: string;
  relatedContactEmails: string[];
};

const defaultValues: CreateEffortRequestFormParams = {
  bookId: null,
  companyVkn: null,
  projectId: null,
  explanation: '',
  effortDay: '',
  estimatedStartDate: null,
  estimatedEndDate: null,
  customerFullName: '',
  customerEmail: '',
  relatedContactEmails: [],
};

type CreateEffortRequestDialogProps = {
  open: boolean;
  handleClose: () => void;
  books: Book[];
  products: Project[];
  refresh: () => Promise<void>;
};

const CreateEffortRequestDialog = ({
  open,
  handleClose: closeDialog,
  books,
  products,
  refresh,
}: CreateEffortRequestDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [aiEstimateLoading, setAiEstimateLoading] = useState(false);
  const [aiEstimateResult, setAiEstimateResult] = useState<number | null>(null);

  // form validations & form state
  const schema = yup
    .object({
      bookId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      companyVkn: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      projectId: yup
        .mixed()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      explanation: yup
        .string()
        .trim()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      effortDay: yup
        .string()
        .trim()
        .required(
          intl.formatMessage({
            id: 'validations.required',
            defaultMessage: 'Bu alan boş bırakılamaz',
          })
        )
        .matches(
          /^(?:0\.[0-9]*[1-9][0-9]*|[1-9][0-9]*(?:\.[0-9]+)?)$/,
          intl.formatMessage({
            id: 'validations.onlyDigits',
            defaultMessage: 'Sadece sayı girilmelidir',
          })
        ),
      estimatedStartDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => dayjs.isDayjs(v) && v.isValid()
        )
        .test(
          'start-not-in-past',
          intl.formatMessage({
            id: 'validations.startDateCannotBePast',
            defaultMessage: 'Başlangıç tarihi geçmiş olamaz',
          }),
          (start) => {
            if (!dayjs.isDayjs(start) || !start.isValid()) return true;

            return !start.isBefore(dayjs().startOf('day'), 'day');
          }
        ),
      estimatedEndDate: yup
        .mixed<Dayjs>()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
        .test(
          'is-dayjs',
          intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
          (v) => dayjs.isDayjs(v) && v.isValid()
        )
        .test(
          'end-after-start',
          intl.formatMessage({
            id: 'validations.mustBeAfterStartDate',
            defaultMessage: 'Başlangıç tarihinden sonrası seçilmelidir',
          }),
          function (end) {
            const start = this.parent.estimatedStartDate as Dayjs | undefined;
            if (!dayjs.isDayjs(start) || !start.isValid()) return true;
            if (!dayjs.isDayjs(end) || !end.isValid()) return false;
            return !end.isBefore(start, 'day');
          }
        )
        .test(
          'range-not-less-than-effortday',
          intl.formatMessage({
            id: 'validations.dateRangeCannotBeLessThanEffortDay',
            defaultMessage: 'Tarih aralığı efor gününden az olamaz',
          }),
          function (end) {
            const start = this.parent.estimatedStartDate as Dayjs | undefined;
            const effortDayRaw = this.parent.effortDay as string | undefined;

            if (!dayjs.isDayjs(start) || !start.isValid()) return true;
            if (!dayjs.isDayjs(end) || !end.isValid()) return true;
            if (!effortDayRaw) return true;

            const effortDay = Number(String(effortDayRaw).trim());
            if (!Number.isFinite(effortDay) || effortDay <= 0) return true;

            const s = start.startOf('day');
            const e = end.startOf('day');

            const rangeDays = e.diff(s, 'day') + 1;

            return rangeDays >= effortDay;
          }
        ),
      customerFullName: yup
        .string()
        .trim()
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      customerEmail: yup
        .string()
        .trim()
        .email(intl.formatMessage({ id: 'validations.invalidEmailFormat', defaultMessage: 'Geçersiz e-posta formatı' }))
        .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
      relatedContactEmails: yup
        .array()
        .of(
          yup.object({
            nameSurname: yup
              .string()
              .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
            email: yup
              .string()
              .trim()
              .email(
                intl.formatMessage({ id: 'validations.invalidEmailFormat', defaultMessage: 'Geçersiz e-posta formatı' })
              )
              .required(
                intl.formatMessage({
                  id: 'validations.required',
                  defaultMessage: 'Bu alan boş bırakılamaz',
                })
              ),
            phoneNumber: yup.string().nullable().notRequired(),
          })
        )
        .test(
          'required-if-book-has-related-contacts',
          intl.formatMessage({
            id: 'effortTracing.validation.atLeastOneRelatedContactRequired',
            defaultMessage: 'En az 1 ilgili kişi seçmelisiniz',
          }),
          function (value) {
            const available = watchBook?.relatedContacts;

            if (!available || available.length === 0) return true;

            return Array.isArray(value) && value.length > 0;
          }
        ),
    })
    .required();

  const { reset, control, watch, setValue, handleSubmit } = useForm<CreateEffortRequestFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // dynamic variables
  const watchBook = watch('bookId');
  const watchEffortDay = watch('effortDay');
  const watchProjectId = watch('projectId');
  const watchExplanation = watch('explanation');
  const watchEstimatedStartDate = watch('estimatedStartDate');

  // async helper functions
  const onSubmit = async (data: CreateEffortRequestFormParams) => {
    if (!data.bookId || !data.companyVkn || !data.projectId || !data.estimatedStartDate || !data.estimatedEndDate)
      return;

    const postData: CreateEffortRequestPostFormParams = {
      ...data,
      bookId: data.bookId.id,
      companyVkn: data.companyVkn.vkn,
      projectId: data.projectId.id,
      effortDay: Number(data.effortDay),
      estimatedStartDate: data.estimatedStartDate.toISOString(),
      estimatedEndDate: data.estimatedEndDate.toISOString(),
      relatedContactEmails: (data.relatedContactEmails ?? []).map((x) => x.email),
    };

    try {
      setLoading(true);

      const response = await apiService.createEffortRequest(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortTracing.createEffortRequestCompleted"
            defaultMessage="Efor talebi oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );

        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setAiEstimateResult(null);
  };

  const handleAiEstimate = async () => {
    if (!watchProjectId || !watchExplanation?.trim()) return;

    try {
      setAiEstimateLoading(true);

      const response = await apiService.getAiEffortEstimate({
        projectId: watchProjectId.id,
        explanation: watchExplanation.trim(),
      });

      if (response.isSuccess) {
        setAiEstimateResult(response.data);
      }
    } catch {
    } finally {
      setAiEstimateLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    setValue('companyVkn', null);
    setValue('projectId', null);
    setValue('relatedContactEmails', []);
  }, [watchBook, setValue]);

  useEffect(() => {
    if (!dayjs.isDayjs(watchEstimatedStartDate) || !watchEstimatedStartDate.isValid()) return;

    const raw = String(watchEffortDay ?? '').trim();
    if (!raw) return;

    const effort = Number(raw);
    if (!Number.isFinite(effort) || effort <= 0) return;

    const start = watchEstimatedStartDate.startOf('day');
    const end = start.add(Math.max(0, Math.ceil(effort) - 1), 'day');

    setValue('estimatedEndDate', end, { shouldValidate: true, shouldDirty: true });
  }, [watchEstimatedStartDate, watchEffortDay, setValue]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="effortTracing.createEffortRequest" defaultMessage="Efor Talebi Oluştur" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="effortTracing.createEffortRequestText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir efor talebi oluşturabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="bookId">
                <FormattedMessage id="effortTracing.bookId" defaultMessage="Proje Seçimi" />
              </FormLabel>
              <Controller
                name="bookId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={books}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Book | null) => onChange(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="companyTitle">
                <FormattedMessage id="effortTracing.companyTitle" defaultMessage="Talep Eden Şirket" />
              </FormLabel>
              <Controller
                name="companyVkn"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!!!watchBook}
                    size="small"
                    options={(() => {
                      const duplicateArr = watchBook?.companies ? [...watchBook?.companies] : [];
                      const sortedData = duplicateArr.sort((a, b) => a.title.localeCompare(b.title));

                      return sortedData;
                    })()}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: CompanyToBeCreated | null) => onChange(newValue)}
                    getOptionLabel={(option: CompanyToBeCreated) => option.title ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="effortTracing.projectId" defaultMessage="İlgili Ürün" />
              </FormLabel>
              <Controller
                name="projectId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!!!watchBook}
                    size="small"
                    options={(() => {
                      const filteredProducts = products.filter((product) =>
                        watchBook?.projects.some((watchBookProduct) => watchBookProduct.id === product.id)
                      );
                      const sortedData = filteredProducts.sort((a, b) => a.title.localeCompare(b.title));

                      return sortedData;
                    })()}
                    value={value}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Project | null) => onChange(newValue)}
                    getOptionLabel={(option: Project) => option.title}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="effortDay">
                <FormattedMessage id="effortTracing.effortDay" defaultMessage="Efor Günü" />
              </FormLabel>
              <Controller
                name="effortDay"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="customerFullName">
                <FormattedMessage id="effortTracing.customerFullName" defaultMessage="Talep Eden Müşteri Ad Soyad" />
              </FormLabel>
              <Controller
                name="customerFullName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="customerEmail">
                <FormattedMessage id="effortTracing.customerEmail" defaultMessage="Talep Eden Müşteri E-Posta" />
              </FormLabel>
              <Controller
                name="customerEmail"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="estimatedStartDate">
                <FormattedMessage id="effortTracing.estimatedStartDate" defaultMessage="Tahmini Başlangıç" />
              </FormLabel>
              <Controller
                name="estimatedStartDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    localeText={renderDatePickerLocaleText()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="estimatedEndDate">
                <FormattedMessage id="effortTracing.estimatedEndDate" defaultMessage="Tahmini Bitiş" />
              </FormLabel>
              <Controller
                name="estimatedEndDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    localeText={renderDatePickerLocaleText()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <FormLabel htmlFor="relatedContactEmails">
                <FormattedMessage id="effortTracing.relatedContactEmails" defaultMessage="İlgili Kişiler" />
              </FormLabel>
              <Controller
                name="relatedContactEmails"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    multiple
                    size="small"
                    disabled={!!!watchBook || !watchBook?.relatedContacts?.length}
                    options={watchBook?.relatedContacts ?? []}
                    value={value ?? []}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue) => onChange(newValue)}
                    isOptionEqualToValue={(opt, val) => opt.email === val.email}
                    getOptionLabel={(option) => `${option.nameSurname} (${option.email})`}
                    filterSelectedOptions
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderTags={(value, getTagProps) => {
                      const maxVisible = 3;
                      const visible = value.slice(0, maxVisible);
                      const hiddenCount = value.length - maxVisible;

                      return (
                        <>
                          {visible.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });

                            return (
                              <Tooltip
                                key={option.email}
                                title={
                                  <>
                                    <div>{option.email}</div>
                                    {option.phoneNumber && <div>{option.phoneNumber}</div>}
                                  </>
                                }
                                arrow>
                                <Chip
                                  {...tagProps}
                                  label={option.nameSurname}
                                  size="small"
                                  sx={{
                                    borderRadius: 1.5,
                                    fontWeight: 500,
                                  }}
                                />
                              </Tooltip>
                            );
                          })}

                          {hiddenCount > 0 && (
                            <Chip
                              label={`+${hiddenCount}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 1.5,
                                fontWeight: 600,
                                bgcolor:
                                  theme.palette.mode === 'light'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.dark,
                                color: theme.palette.primary.contrastText,
                              }}
                            />
                          )}
                        </>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={intl.formatMessage({
                          id: 'effortTracing.relatedContactsPlaceholder',
                          defaultMessage: 'Kişi seçin',
                        })}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            width: 'fit-content',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                            '& .MuiInputBase-root': {
                              height: 'auto',
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <FormLabel htmlFor="explanation">
                <FormattedMessage id="effortTracing.explanation" defaultMessage="Efor Açıklaması" />
              </FormLabel>
              <Controller
                name="explanation"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={4}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={aiEstimateLoading ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
              disabled={!watchProjectId || !watchExplanation?.trim() || aiEstimateLoading}
              onClick={handleAiEstimate}>
              <FormattedMessage id="effortTracing.aiEstimateEffort" defaultMessage="AI ile Tahmini Efor Analizi" />
            </Button>
            {aiEstimateResult !== null && (
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                {aiEstimateResult}{' '}
                <FormattedMessage id="effortTracing.aiEstimateEffortDaySuffix" defaultMessage="gün" />
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose}>
              <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
            </Button>
            <Button variant="contained" type="submit">
              <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
            </Button>
          </Box>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateEffortRequestDialog;
